import axios from './utils/axios';

const pathPrefix = '/auth/activation';

export type ActivationAuthMethod = 'password' | 'sso';

export interface ActivationDetails {
  email: string;
  auth_method: ActivationAuthMethod;
  /**
   * Auth0 organization for SSO recipients, resolved from the tenant by the
   * backend so the invitation link does not have to carry tenant routing.
   */
  organization?: string | null;
  tenant_slug?: string | null;
  /**
   * SSO links stay usable after first use, but a used token can no longer set
   * a password, so the page offers to email a fresh password link instead.
   */
  used?: boolean;
}

export interface ActivationSetPasswordRequest {
  token: string;
  new_password: string;
  new_password_confirm: string;
}

export interface ActivationVerifyResponse extends ActivationDetails {
  valid?: boolean;
  expired?: boolean;
  isSSO?: boolean;
}

export interface ActivationResendRequest {
  token: string;
  isSSO?: boolean;
}

export const ActivationService = {
  /** Errors are rendered as the expired state, so the global toast is skipped. */
  verifyToken: async (token: string): Promise<ActivationDetails> => {
    const { data } = await axios.get<ActivationVerifyResponse>(
      `${pathPrefix}/verify/${token}/`,
      { suppressErrorToast: true, skipUnauthorizedRedirect: true }
    );

    if (data?.expired === true || data?.valid === false) {
      const error = Object.assign(new Error('Invitation expired'), {
        response: {
          data: {
            ...data,
            code: data?.expired === true ? 'expired' : 'invalid',
            error:
              data?.expired === true
                ? 'Activation token has expired.'
                : 'Invalid activation token.',
          },
        },
      });
      throw error;
    }

    return data;
  },

  setPassword: async (payload: ActivationSetPasswordRequest) => {
    const { data } = await axios.post(`${pathPrefix}/set-password/`, payload);
    return data;
  },

  resendInvitation: async (payload: ActivationResendRequest) => {
    const { data } = await axios.post(`${pathPrefix}/resend/`, payload, {
      suppressErrorToast: true,
      skipUnauthorizedRedirect: true,
    });
    return data;
  },
};
